import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Card, CardContent, Stack, Typography, Skeleton, Pagination
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Partner } from "@kappa-v2/contracts";
import { ActiveChip, PartnerTypeChip } from "../../../components/StatusChip.js";
import { DataGridRowActionsMenu } from "../../../components/DataGridRowActionsMenu.js";
import { buildPartnerRowActionItems } from "./partnerRowActions.js";

const PAGE_SIZE = 15;

interface PartnersMobileCardsProps {
  rows: Partner[];
  loading: boolean;
  onEdit: (p: Partner) => void;
  onDelete: (p: Partner) => void;
  onToggleActive: (p: Partner) => void;
  onEnroll?: (p: Partner) => void;
}

function getMainContact(row: Partner) {
  return (
    row.contacts?.find((c) => c.isMain)?.telephoneNumber ??
    row.contacts?.[0]?.telephoneNumber ??
    ""
  );
}

function getMainEmail(row: Partner) {
  return row.emails?.find((e) => e.isMain)?.email ?? row.emails?.[0]?.email ?? "";
}

export function PartnersMobileCards({
  rows,
  loading,
  onEdit,
  onDelete,
  onToggleActive,
  onEnroll
}: PartnersMobileCardsProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const slice = useMemo(() => {
    const safePage = Math.min(page, pageCount);
    const start = (safePage - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, page, pageCount]);

  useEffect(() => {
    setPage(1);
  }, [rows.length]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  if (loading) {
    return (
      <Stack spacing={1.5} sx={{ p: 2 }}>
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} variant="rounded" height={96} sx={{ borderRadius: 2 }} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack spacing={0}>
      <Stack spacing={1.5} sx={{ p: 2 }}>
        {slice.map((row) => {
          const id = row._id ?? `${row.lastName}-${row.firstName}`;
          const phone = getMainContact(row);
          const email = getMainEmail(row);
          return (
            <Card
              key={id}
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: "rgba(0,0,0,.08)",
                bgcolor: "background.paper"
              }}
            >
              <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={700} component="div" noWrap>
                      {row.lastName} {row.firstName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      CF {row.fiscalCode ?? "—"}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                      <PartnerTypeChip type={row.partnerType} />
                      <ActiveChip active={row.active} />
                    </Stack>
                    {phone ? (
                      <Typography variant="body2" color="text.primary">
                        {phone}
                      </Typography>
                    ) : null}
                    {email ? (
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ display: "block" }}>
                        {email}
                      </Typography>
                    ) : null}
                    {!phone && !email ? (
                      <Typography variant="body2" color="text.disabled">
                        {t("partners.contact")}: —
                      </Typography>
                    ) : null}
                  </Box>
                  <DataGridRowActionsMenu
                    ariaLabel={`${t("common.actions")} — ${row.lastName} ${row.firstName}`}
                    items={buildPartnerRowActionItems(row, {
                      onEdit,
                      onDelete,
                      onToggleActive,
                      onEnroll
                    }, t)}
                  />
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Stack>
      {rows.length > PAGE_SIZE ? (
        <Box sx={{ display: "flex", justifyContent: "center", pb: 2, px: 2 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="small"
            siblingCount={0}
            boundaryCount={1}
          />
        </Box>
      ) : null}
    </Stack>
  );
}
